import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { partnerSettingsKeys } from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import { getPartnerRangeParams } from "@/components/pages/guesteria-partners/utils/partnerAnalyticsPeriod";
import type { PartnerRevenueDateRange } from "@/components/pages/guesteria-partners/utils/partnerRevenuePeriodPresets";
import { useHotel } from "@/hooks/useHotel";
import { partnerSettingsService } from "@/services/partner-settings-service";
import type { PartnerProviderType } from "@/types/partner-settings";
import { is404 } from "@/utils/httpErrors";

// `provider` is only present for SUPER_ADMIN/GUESTERIA_ADMIN viewers (task
// 0028, contract 2026-09-28) — every other role gets `source:
// "guesteria_partners"` instead, so this must not gate the query on it.
// Task 0038 (owner, 2026-09-30): `range` comes straight from the shared
// `usePartnerRevenuePeriod`, no more named period.
export const usePartnerOrdersSummary = (
  range: PartnerRevenueDateRange,
  provider: PartnerProviderType | undefined,
  enabled = true,
) => {
  const { hotelId } = useHotel();
  const periodParams = useMemo(() => getPartnerRangeParams(range), [range]);
  const periodKey = `${periodParams.from}_${periodParams.to}`;

  return useQuery({
    queryKey: partnerSettingsKeys.ordersSummary(
      hotelId || "",
      periodKey,
      provider || "",
    ),
    enabled: Boolean(hotelId) && enabled,
    queryFn: async () => {
      try {
        return await partnerSettingsService.getPartnerOrdersSummary(hotelId!, {
          from: periodParams.from,
          to: periodParams.to,
          provider,
        });
      } catch (e) {
        if (is404(e)) {
          return null;
        }
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};
